import { useEffect, useRef } from 'react'
import { NavLink, Route, Routes, useLocation } from 'react-router'
import { api } from './lib/api'
import { Dashboard } from './pages/Dashboard'
import { Developer } from './pages/Developer'
import { EmailTemplate } from './pages/EmailTemplate'
import { Onboarding } from './pages/Onboarding'
import { Plans } from './pages/Plans'
import { Recommendations } from './pages/Recommendations'
import { Settings } from './pages/Settings'
import { Stockouts } from './pages/Stockouts'
import { Support } from './pages/Support'
import { VariantDetail, Waitlist } from './pages/Waitlist'

/**
 * The app shell (SW-601).
 *
 * Client-side routing with `<s-page>` as the frame, so the back button and deep links work
 * inside the admin iframe, which they must, because a merchant who bookmarks a screen and
 * gets a blank page reads that as the app being broken.
 */

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/waitlist', label: 'Waitlists' },
  { to: '/recommendations', label: 'Restock plan' },
  { to: '/stockouts', label: 'Stockout costs' },
  { to: '/email', label: 'Email' },
  { to: '/settings', label: 'Settings' },
  { to: '/plans', label: 'Plans' },
  { to: '/developer', label: 'API' },
  { to: '/support', label: 'Support' },
]

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/waitlist': 'Waitlists',
  '/recommendations': 'Restock plan',
  '/stockouts': 'Stockout costs',
  '/email': 'Alert email',
  '/settings': 'Settings',
  '/plans': 'Plans and usage',
  '/developer': 'API and webhooks',
  '/support': 'Support',
  '/onboarding': 'Set up StockWait',
}

/**
 * Forces a plan refresh when the merchant returns from Shopify's hosted plan page.
 *
 * Shopify redirects back with `?charge_id=…&plan_handle=…`, which is it telling us outright
 * that the plan just changed. The background refresh on admin load is rate-limited to once an
 * hour, correct for ordinary page loads, wrong for this one: a merchant who just paid would
 * see their OLD plan for up to an hour, which is the same "they pay and nothing unlocks"
 * failure the hourly refresh was built to fix, only compressed.
 *
 * Observed in production: selecting Intelligence left the row on `free` because the cached plan
 * was nine minutes old.
 */
function usePlanRefreshOnReturn() {
  const location = useLocation()
  const done = useRef(false)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (!params.has('charge_id') && !params.has('plan_handle')) return
    // Once per mount: the param survives in the URL, and re-firing on every render would put
    // an Admin API call behind every state change.
    if (done.current) return
    done.current = true

    api
      .post('/plans/refresh')
      // The merchant is looking at a freshly upgraded app; a full reload is the simplest way to
      // make every gated screen reflect the new plan at once.
      .then(() => window.location.replace(window.location.pathname))
      .catch(() => {
        // Never block the return trip. The hourly refresh and the nightly sweep are both still
        // behind this, so the plan corrects itself either way.
      })
  }, [location.search])
}

export function App() {
  const location = useLocation()
  const heading = TITLES[location.pathname] ?? 'StockWait'
  usePlanRefreshOnReturn()

  return (
    <s-page heading={heading}>
      {/*
        `ui-nav-menu` is App Bridge's own navigation element; it renders in Shopify's admin
        chrome rather than inside the app frame, which is what makes the app feel native
        rather than like a website in an iframe.
      */}
      <ui-nav-menu>
        <a href="/" rel="home">
          StockWait
        </a>
        {NAV.slice(1).map((item) => (
          <a key={item.to} href={item.to}>
            {item.label}
          </a>
        ))}
      </ui-nav-menu>

      <s-stack direction="block" gap="large">
        {/* In-app fallback nav: the admin chrome menu is not present in every context,
            notably the Shopify mobile app. */}
        <s-stack direction="inline" gap="small-300">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} style={{ textDecoration: 'none' }}>
              {({ isActive }) =>
                isActive ? (
                  <s-button variant="primary">{item.label}</s-button>
                ) : (
                  <s-button variant="tertiary">{item.label}</s-button>
                )
              }
            </NavLink>
          ))}
        </s-stack>

        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/waitlist/:variantId" element={<VariantDetail />} />
          <Route path="/recommendations" element={<Recommendations />} />
          <Route path="/stockouts" element={<Stockouts />} />
          <Route path="/email" element={<EmailTemplate />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/developer" element={<Developer />} />
          <Route path="/support" element={<Support />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route
            path="*"
            element={
              <s-section>
                <s-banner tone="warning" heading="Page not found">
                  That screen doesn't exist. Try the dashboard.
                </s-banner>
              </s-section>
            }
          />
        </Routes>
      </s-stack>
    </s-page>
  )
}
